/**
 * Voice Providers Management Page
 *
 * Displays all TTS providers with voice browsing and management.
 * API key configuration is handled via the Provider Credentials page.
 *
 * Acceptance Criteria: AC 23-25, 34
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useProviders, type TTSProvider } from '../services/voiceApi';
import { VoiceBrowserModal } from '../components/VoiceBrowserModal';
import { VoiceNavigation } from '../components/VoiceNavigation';

export function VoiceProvidersView() {
  const navigate = useNavigate();
  const { data: providers, isLoading, error } = useProviders();
  const [selectedProvider, setSelectedProvider] = useState<TTSProvider | null>(null);
  const [showBrowserModal, setShowBrowserModal] = useState(false);

  const handleBrowseVoices = (provider: TTSProvider) => {
    setSelectedProvider(provider);
    setShowBrowserModal(true);
  };

  const handleViewAddedVoices = (provider: TTSProvider) => {
    // Navigate to voices list filtered by provider
    navigate(`/voices?provider=${provider.id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading providers</p>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <VoiceNavigation />
      <h1 className="text-3xl font-bold mb-8">TTS Providers</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {providers?.map((provider) => (
          <div
            key={provider.id}
            className="bg-white rounded-lg shadow-md p-6 border border-gray-200"
            data-testid={`provider-card-${provider.id}`}
          >
            {/* Provider Name */}
            <h2 className="text-xl font-semibold mb-4">{provider.name}</h2>

            {/* API Key Status */}
            <div className="mb-4">
              {provider.hasApiKey ? (
                <div className="flex items-center text-green-600" data-testid="api-key-configured">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>API Key Configured</span>
                </div>
              ) : (
                <div className="flex items-center text-amber-600" data-testid="api-key-missing">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>
                    No API Key —{' '}
                    <Link to="/provider-credentials" className="underline hover:text-amber-800">
                      configure in Provider Credentials
                    </Link>
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <button
                onClick={() => handleBrowseVoices(provider)}
                disabled={!provider.hasApiKey}
                className={`w-full font-medium py-2 px-4 rounded transition-colors ${
                  provider.hasApiKey
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                data-testid="browse-voices-button"
              >
                Browse Voices
              </button>

              <button
                onClick={() => handleViewAddedVoices(provider)}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded transition-colors"
                data-testid="view-added-voices-button"
              >
                View Added Voices
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modals */}
      {selectedProvider && (
        <VoiceBrowserModal
          isOpen={showBrowserModal}
          onClose={() => {
            setShowBrowserModal(false);
            setSelectedProvider(null);
          }}
          provider={selectedProvider}
        />
      )}
    </div>
  );
}
